import {
  AssistantCopilotAction,
  AssistantGrounding,
  AssistantViewModel,
} from "@/services/modules/assistant";
import { GroundingMeta } from "./GroundingMeta";
import { cn } from "@/lib/utils";
import { ArrowRight, Zap } from "lucide-react";

type Props = {
  viewModel: AssistantViewModel;
  grounding?: AssistantGrounding;
  onAction: (action: AssistantCopilotAction) => void;
};

export function CopilotCard({ viewModel, grounding, onAction }: Props) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm w-full">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-blue-500 text-white shadow-sm">
          <Zap className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-600">
                {viewModel.mode === "copilot" ? "Copiloto contextual" : "Assistente"}
              </div>
              <div className="mt-1 text-[14px] font-semibold text-slate-900">
                {viewModel.title || "Leitura operacional pronta"}
              </div>
            </div>
            {viewModel.contextLabel ? (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-medium text-slate-500">
                {viewModel.contextLabel}
              </span>
            ) : null}
          </div>

          <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-[12px] leading-relaxed text-slate-700">
            {viewModel.summary}
          </div>

          {viewModel.findings && viewModel.findings.length > 0 ? (
            <div className="mt-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                O que detectei
              </div>
              <div className="mt-2 space-y-2">
                {viewModel.findings.map((item, index) => (
                  <div
                    key={`${item}-${index}`}
                    className="flex items-start gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2 text-[12px] text-slate-700 shadow-sm"
                  >
                    <span className="mt-[5px] h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
                    <span className="flex-1">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {viewModel.nextStep ? (
            <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] font-medium text-emerald-800">
              Próximo passo: {viewModel.nextStep}
            </div>
          ) : null}

          {viewModel.actions && viewModel.actions.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {viewModel.actions.map((action, index) => (
                <button
                  key={`${action.label}-${index}`}
                  type="button"
                  onClick={() => onAction(action)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors shadow-xs",
                    action.tone === "primary"
                      ? "border-blue-500 bg-blue-500 text-white hover:bg-blue-600"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  )}
                >
                  {action.label}
                  <ArrowRight className="h-3 w-3" />
                </button>
              ))}
            </div>
          ) : null}

          <GroundingMeta grounding={grounding} />
        </div>
      </div>
    </div>
  );
}
